<template>
  <v-container>
    <v-row>
      <v-col>
        <!-- Action Buttons -->
        <v-card-actions class="mb-4">
          <v-spacer />
          <v-btn
            v-if="isReviewing"
            color="primary"
            depressed
            :loading="loading"
            :disabled="hasBeenCreated || canReview"
            @click="create"
          >
            Create Pages
          </v-btn>
          <v-btn
            v-if="isReviewing"
            color="info"
            depressed
            :loading="loading"
            :disabled="hasBeenCreated || canReview"
            @click="onDecrementStep"
          >
            Continue Edit
          </v-btn>
          <v-btn
            v-if="!isReviewing"
            color="primary"
            depressed
            :loading="loading"
            :disabled="!canReview"
            @click="onIncrementStep"
          >
            Review
          </v-btn>
          <v-btn color="error" :loading="loading" depressed @click="reset">Reset</v-btn>
        </v-card-actions>

        <!-- Reviewing Page Content -->
        <page-review-list
          v-if="isReviewing"
          :pages="hasBeenCreated ? pages : transformedPayload"
          :page-ids="pageIds"
          :page-types="pageTypes && pageTypes.data"
          :sites="sites && sites.data"
          :languages="languages && languages.data"
          :entities="entities && entities.data"
          :has-been-created="hasBeenCreated"
          :loading="loading"
        />

        <!-- Reviewing Page Content -->
        <v-stepper v-else v-model="step" vertical>
          <stepper-page-type-step
            v-model="formData.page_type_id"
            :complete="pageTypeSelected"
            :page-types="pageTypes.data"
            step="1"
            :loading="loading"
            @change="onPageTypeChange"
            @increment="onIncrementStep"
          />

          <stepper-site-step
            v-model="formData.sites"
            :complete="sitesSelected"
            :page-type-id="formData.page_type_id"
            :page-type-site-groups="pageTypeSiteGroups.data"
            :sites="sites.data"
            :current-step="parseInt(step)"
            step="2"
            :loading="loading"
            @change="onSitesChange"
            @increment="onIncrementStep"
          />

          <stepper-language-step
            v-for="(siteId, index) of Object.keys(formData.sites)"
            :key="index"
            v-model="formData.sites[siteId]"
            :site-id="siteId"
            :sites="sites.data"
            :language-sites="languageSites.data"
            :current-step="parseInt(step)"
            :form-data="formData"
            :show-button="canShowLanguageContinueButton(index + 1)"
            step="3"
            :loading="loading"
            @change="languages => onLanguagesChange(siteId, languages)"
            @increment="onIncrementStep"
          />

          <template v-for="(siteId, index) of Object.keys(formData.sites)">
            <stepper-entity-step
              v-for="(languageId, key) of Object.keys(formData.sites[siteId])"
              :key="`${index}.${key}`"
              v-model="formData.sites[siteId][languageId]"
              :site-id="siteId"
              :language-id="languageId"
              :sites="sites.data"
              :languages="languages.data"
              :current-step="parseInt(step)"
              :entity-type-page-types="entityTypePageTypes.data"
              :entities="entities.data"
              :form-data="formData"
              step="4"
              :loading="loading"
              @change="entities => onEntititesChange(siteId, languageId, entities)"
              @apply="onApplyToAll(siteId, languageId)"
              @increment="onIncrementStep"
            />
          </template>
        </v-stepper>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import StepperPageTypeStep from './components/wizard/stepper-page-type-step'
import StepperSiteStep from './components/wizard/stepper-site-step'
import StepperLanguageStep from './components/wizard/stepper-language-step'
import StepperEntityStep from './components/wizard/stepper-entity-step'
import PageReviewList from './components/wizard/page-review-list'

import { entities, pages } from '@/services/offers'

export default {
  components: { PageReviewList, StepperPageTypeStep, StepperSiteStep, StepperLanguageStep, StepperEntityStep },
  data: () => ({
    loading: false,
    step: '1',
    entities: [],
    pages: [],
    pageIds: [],
    formData: {
      page_type_id: null,
      sites: {},
    },
  }),
  computed: {
    ...mapGetters({
      pageTypes: 'pageTypes/getPageTypes',
      pageTypeSiteGroups: 'pageTypeSiteGroups/getPageTypeSiteGroups',
      sites: 'sites/getSites',
      languageSites: 'languageSites/getLanguageSites',
      languages: 'languages/getLanguages',
      entityTypePageTypes: 'entityTypePageTypes/getEntityTypePageTypes',
    }),
    pageTypeSelected() {
      return !!this.formData.page_type_id
    },
    sitesSelected() {
      return !!Object.keys(this.formData.sites).length
    },
    canReview() {
      return this.step == 4
    },
    isReviewing() {
      return this.step == 5
    },
    hasBeenCreated() {
      return !!this.pages.length
    },
    transformedPayload() {
      const pages = []
      const { page_type_id, sites } = this.formData
      const pageType = this.pageTypes.data.find(({ id }) => id == page_type_id)
      for (const site_id of Object.keys(sites)) {
        const site = this.sites.data.find(({ id }) => id == site_id)
        for (const language_id of Object.keys(sites[site_id])) {
          const language = this.languages.data.find(({ id }) => id == language_id)
          for (const entityIds of sites[site_id][language_id]) {
            const entities = entityIds.map(entityId => this.entities.data.find(({ id }) => id == entityId))
            pages.push({
              page_type_id,
              pageType,
              site_id,
              site,
              language_id,
              language,
              entities,
              entityIds,
            })
          }
        }
      }
      return pages
    },
  },
  mounted() {
    this.reset()
  },
  methods: {
    ...mapActions({
      fetchPageTypes: 'pageTypes/fetch',
      fetchPageTypeSiteGroups: 'pageTypeSiteGroups/fetch',
      fetchSites: 'sites/fetch',
      fetchLanguageSites: 'languageSites/fetch',
      fetchLanguages: 'languages/fetch',
      fetchEntityTypePageTypes: 'entityTypePageTypes/fetch',
    }),
    onDecrementStep() {
      this.step--
    },
    onIncrementStep() {
      this.step++
    },
    onPageTypeChange(page_type_id) {
      this.formData.page_type_id = page_type_id
    },
    onSitesChange(sites) {
      this.formData.sites = sites
    },
    onLanguagesChange(siteId, languages) {
      this.formData.sites[siteId] = languages
    },
    onEntititesChange(siteId, languageId, entities) {
      this.formData.sites[siteId][languageId] = entities
    },
    canShowLanguageContinueButton(index) {
      return Object.keys(this.formData.sites).length === index
    },
    onApplyToAll(siteId, languageId) {
      const entities = this.formData.sites[siteId][languageId]
      for (const site of Object.keys(this.formData.sites)) {
        for (const language of Object.keys(this.formData.sites[site])) {
          this.formData.sites[site][language] = entities
        }
      }
    },
    async reset() {
      this.loading = true
      const paginate = { perPage: null }
      await this.fetchPageTypes({ paginate })
      await this.fetchPageTypeSiteGroups({ paginate })
      await this.fetchSites({ paginate })
      await this.fetchLanguageSites({ paginate })
      await this.fetchLanguages({ paginate })
      await this.fetchEntityTypePageTypes({ paginate })
      const data = await entities.getEntities({})

      this.entities = data
      this.pages = []
      this.pageIds = []
      this.step = 1
      this.loading = false
      this.formData.page_type_id = null
      this.formData.sites = {}
    },
    async create() {
      this.loading = true
      await pages
        .bulkCreate(this.transformedPayload)
        .then(async pageIds => {
          this.pageIds = pageIds
          const { data } = await pages.getAll({
            filters: { idIn: pageIds.map(({ id }) => id) },
            paginate: { perPage: null },
          })
          this.pages = data
          this.loading = false
          this.$notify({
            type: 'success',
            text:
              'These pages have now been created, you can now click to go into the pages or copy the links of the pages to your clipboard',
          })
        })
        .catch(err => {
          this.loading = false
          this.$notify({ type: 'error', text: 'There was an unexpected issue with creating your pages.' })
        })
    },
  },
}
</script>
